import { useState, useEffect, useMemo, useRef } from "preact/hooks"
import { Button } from "../../../components/ui/button"
import { Checkbox } from "../../../components/ui/checkbox"
import { Input } from "../../../components/ui/input"
import { DecimalInput } from "../../../components/ui/decimal_input"
import { Label } from "../../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../../components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog"
import { CheckCircle2, Plus, X } from "lucide-react"
import type { SalesHierarchyNode } from "../types"
import { TimePeriod } from "../types"
import { SearchableSelect, SearchableSelectOption } from "../../../components/searchable_select"
import { api_fetch_throws } from "../../../api/client"
import { getProductDisplayLabel, listProductLines, product_upc, searchProducts, type ProductLine, type ProductSearchResult } from "../../../api/product_catalog"
import { exact_catalog_match } from "../utils/catalog_resolve"
import { SuggestedPriceCaption } from "./suggested_price_caption"
import { DoorsByMonthEditor } from "./doors_by_month_editor"
import { useAsyncEffect } from "../../../utils"
import { analyzeSalesSourceCollision } from "../utils/sales_source_collision"
import { MONTHS_PER_WEEK, format_period_header, iso_week_key_from_date } from "../utils/periods"
import { sku_stable_key_for_expectation } from "../utils/data_generator"
import { case_basis_hint, plan_quantity_basis, sku_plan_reference } from "../utils/quantity_basis"
import {
  customer_kind_hint,
  customer_routes_through_distributor,
  distributor_split_error,
  growth_inputs_for_customer,
  type CustomerKindHint,
  type CustomerKindRecord,
} from "../utils/customer_kind"
import { preview_sales_source, type PreviewPeriod, type SalesSourcePreview } from "../utils/sales_source_preview"


let growth_expectation_seq = 0
/**
 * A row id that is unique even when two rows are added in the same millisecond: `growth_<ms>`
 * alone collided on a double-click of "Add Growth Expectation", and two rows sharing one id edit
 * as one (every keystroke landed in both) and render with duplicate keys.
 */
export function next_growth_expectation_id(now: number = Date.now()): string {
  growth_expectation_seq += 1
  return `growth_${now}_${growth_expectation_seq}`
}

const CLASS_OF_TRADE_OPTIONS = [
  "Natural/Specialty",
  "Conventional", 
  "DSD",
  "Mass",
  "Club",
  "C-Stores / Drug",
  "Foodservice",
  "Military",
  "E-commerce",
  "DTC"
]

const ROUTE_TO_MARKET_OPTIONS = [
  "Direct to Retailer",
  "Indirect via Distributor", 
  "DSD",
  "Broadliner",
  "Foodservice",
  "E-commerce marketplace",
  "DTC"
]

/**
 * Where a new source starts unless told otherwise: the period today falls in — this month on a
 * monthly plan, this ISO week on a weekly one — or the plan's first period when today is outside it.
 */
export function default_start_period_key(periods: TimePeriod[], now: Date): string {
  const month_key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
  const current = [month_key, iso_week_key_from_date(now)]
  return (
    periods.find((p: TimePeriod) => current.includes(p.period_key))?.period_key || periods[0]?.period_key || ""
  )
}

/** `periods` from `start_key` onward — the only periods a source can end on. All of them when the start is unset. */
export function periods_from(periods: TimePeriod[], start_key: string | undefined | null): TimePeriod[] {
  const index = periods.findIndex((p: TimePeriod) => p.period_key === (start_key ?? "").trim())
  return index === -1 ? periods : periods.slice(index)
}

/** True when trimmed key exists in the forecast period list (Radix Select + save validation). */
export function growth_start_month_is_valid(
  startMonth: string | undefined | null,
  periods: TimePeriod[]
): boolean {
  const sm = (startMonth ?? "").trim()
  return sm !== "" && periods.some((p: TimePeriod) => p.period_key === sm)
}

/** True when the plan's columns are weeks, which changes what the volume fields are called and take. */
export function periods_are_weekly(periods: TimePeriod[]): boolean {
  return periods.some((p: TimePeriod) => p.grain === "week")
}

/**
 * The cases-per-store number to show. The form holds the rate per **month** (what the generator
 * and saved configs use); on a weekly plan the field shows and takes the weekly rate instead, so
 * nobody has to multiply by 4.35 to plan a week. Four decimals keeps a typed `0.685` intact while
 * dropping the float dust of the round trip.
 */
export function cases_per_store_input_value(cases_per_month: number, weekly: boolean): number {
  if (!weekly) return cases_per_month
  return Math.round(cases_per_month * MONTHS_PER_WEEK * 10000) / 10000
}

/** Inverse of `cases_per_store_input_value`: what the form stores for a typed rate. */
export function cases_per_month_from_input(value: number, weekly: boolean): number {
  return weekly ? value / MONTHS_PER_WEEK : value
}

/** Select value for "no end period" — Radix Select items cannot carry an empty string. */
const NO_END_PERIOD = "__no_end__"

/** Save validation for the create-sales-source form. Returns a user-facing error, or null when valid. */
export function sales_source_validation_error(
  formData: CreateSalesSourceFormData,
  periods: TimePeriod[]
): string | null {
  if (!formData.customer) {
    return "Please select or enter a customer (required)."
  }
  if (formData.growthExpectations.length === 0) {
    return "Add at least one growth expectation (required)."
  }
  if (periods.length === 0) {
    return "Forecast months are still loading. Wait a moment and try again."
  }
  const invalidExpectations = formData.growthExpectations.filter((exp: GrowthExpectation) => {
    const baseFieldsValid =
      Boolean(exp.productLineName?.trim()) &&
      exp.initialDoors > 0 &&
      exp.initialCasesPerMonth > 0 &&
      exp.costPerCase > 0 &&
      growth_start_month_is_valid(exp.startMonth, periods)
    return !baseFieldsValid
  })
  const span = periods_are_weekly(periods) ? "week" : "month"
  if (invalidExpectations.length > 0) {
    return `Fill in all required growth fields (product line, start ${span}, initial doors, cases per store per ${span}, price per case). Start ${span} must match a ${span} in this forecast.`
  }
  const ends_before_start = formData.growthExpectations.some((exp: GrowthExpectation) => {
    const end = (exp.endMonth ?? "").trim()
    if (!end) return false
    const start_index = periods.findIndex((p: TimePeriod) => p.period_key === exp.startMonth)
    const end_index = periods.findIndex((p: TimePeriod) => p.period_key === end)
    return end_index !== -1 && end_index < start_index
  })
  if (ends_before_start) {
    return `End ${span} is before the start ${span}.`
  }
  const split_error = distributor_split_error(formData.rtmDistributors)
  if (split_error) {
    return split_error
  }
  return null
}

// Fallback list of popular customers when API returns no results
const FALLBACK_CUSTOMERS = [
  // Distributors
  "UNFI",
  "KeHE",
  "C&S",
  "Bozzuto's",
  // Retailers
  "Whole Foods",
  "Sprouts",
  "Kroger",
  "Albertson's",
  "Wakefern",
  "Publix",
  "Safeway",
  "Harris Teeter",
  "The Fresh Market",
  "Natural Grocers"
]

/** A row of `/customers`: the fields the dialog reads (the record carries many more). */
type Customer = CustomerKindRecord

export interface GrowthExpectation {
  id: string
  name: string
  initialDoors: number
  startMonth: string
  /**
   * Last period with volume (inclusive) — a period key like `startMonth`, a week on weekly plans.
   * Later periods are written as zeros. Unset means the source runs through the plan.
   */
  endMonth?: string
  storeCountGrowthMultiplier: number
  productLineName: string
  /** Cases **per store** per month. Weekly plans show and take this as a weekly rate. */
  initialCasesPerMonth: number
  costPerCase: number
  sameStoreSalesMultiplier: number
  // SKU fields (only used when planning by SKU)
  productSku?: string
  skuName?: string
  // Catalog identity from the SKU picker — drives the node's `sku:<upc>` stable key
  // so actualization can land QuickBooks dollars on the row.
  productId?: string
  productUpc?: string
  unitsPerCase?: number
  /** The free-text SKU fields only render after explicitly choosing "Other" in the picker. */
  skuIsOther?: boolean
  /** Door counts typed for particular months (period key → doors), overriding growth there. */
  doorsByPeriod?: Record<string, number>
}

/** One distributor's share of this customer's volume (percent), for the ship-to view. */
export interface RtmDistributorSplitRow {
  name: string
  allocation_pct: number
}

export interface CreateSalesSourceFormData {
  classOfTrade: string
  customer: string
  routeToMarket: string
  customerIsOther: boolean
  planningBySku: boolean | null // kept for API compatibility; derived automatically from SKU fields
  isOpportunity: boolean
  opportunityName: string
  opportunityLikelihood: number
  seasonalityEnabled?: boolean
  /** Jan..Dec, percent of baseline volume (100 = flat month). */
  seasonalityFactorsPct?: number[]
  /** Distributor split written to `metadata.rtm` (drives the Ship-to view grouping). */
  rtmDistributors?: RtmDistributorSplitRow[]
  growthExpectations: GrowthExpectation[]
}

export const SEASONALITY_MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]

export const FLAT_SEASONALITY_PCT: number[] = Array(12).fill(100)

/** Common shapes so a profile is one click; values remain editable per month afterwards. */
export const SEASONALITY_PRESETS: { label: string; factors_pct: number[] }[] = [
  { label: "Flat", factors_pct: FLAT_SEASONALITY_PCT },
  // The beverage pattern Sanzo plans with in Confido: soft January, Q4 winter dip.
  { label: "Beverage (winter dip)", factors_pct: [90, 100, 100, 100, 100, 100, 100, 100, 100, 85, 85, 85] },
  { label: "Summer peak", factors_pct: [85, 85, 95, 100, 110, 120, 125, 120, 105, 95, 85, 85] },
  { label: "Holiday peak", factors_pct: [90, 90, 95, 95, 100, 100, 100, 100, 105, 110, 125, 140] },
]

interface SkuSearchSelectProps {
  onValueChange: (next: {
    product_sku: string
    sku_name: string
    list_price?: number
    product_id?: string
    product_upc?: string
    units_per_case?: number
    product_line?: string
  }) => void
  /** Chosen "Other — type SKU manually": reveals the free-text SKU fields. */
  onOther: () => void
  /** The row's product line, when one is picked: only its products are offered. */
  product_line?: string
}

/** Searchable select using shared SearchableSelect (same interaction model as promo product search). */
function SkuSearchSelect(props: SkuSearchSelectProps) {
  const [query, setQuery] = useState("")
  const [hits, setHits] = useState<ProductSearchResult[]>([])
  const [searching, setSearching] = useState(false)

  const product_line = props.product_line?.trim() ?? ""
  useEffect(() => {
    const timer = setTimeout(() => {
      void (async () => {
        setSearching(true)
        const res = await searchProducts(query.trim(), 25, { product_line })
        if (res.ok) {
          setHits(res.value.data)
        } else {
          setHits([])
        }
        setSearching(false)
      })()
    }, 250)
    return () => clearTimeout(timer)
  }, [query, product_line])

  const options: SearchableSelectOption[] = [
    ...hits.map((p: ProductSearchResult) => ({
      value: p.id,
      label: getProductDisplayLabel(p),
    })),
    { value: "__other__", label: "Other — type SKU manually" },
  ]

  function handleSelect(productId: string): void {
    if (productId === "__other__") {
      props.onOther()
      return
    }
    const selected = hits.find((p: ProductSearchResult) => p.id === productId)
    if (!selected) return
    props.onValueChange({
      product_sku: selected.sku || "",
      sku_name: selected.name,
      list_price: selected.list_price,
      product_id: selected.id,
      // The UPC (not whichever identifier comes first) — the identity forecast_actualize resolves.
      product_upc: product_upc(selected),
      units_per_case: selected.units_per_case,
      product_line: selected.product_line,
    })
  }

  return (
    <SearchableSelect
      options={options}
      value=""
      onValueChange={handleSelect}
      onSearchChange={setQuery}
      placeholder="Search catalog SKU…"
      searchPlaceholder="Type to search SKU or product name…"
      emptyMessage={searching ? "Searching…" : product_line ? `No products found in ${product_line}` : "No products found"}
    />
  )
}

interface CreateSalesSourceDialogProps {
  onSave: (data: CreateSalesSourceFormData) => void
  periods: TimePeriod[]
  /** Current forecast hierarchy — used to warn before additive merge or overlapping opportunity paths. */
  existing_hierarchy_roots?: SalesHierarchyNode[]
  initialData?: Partial<CreateSalesSourceFormData> | null
  /**
   * Which customer kind the dialog opens on. The New menu uses this so "New ship-to customer"
   * lands on the ship-to form instead of making the user find the toggle.
   */
  initialCustomerKind?: "consumption" | "ship_to"
  openSignal?: number | null
  triggerClassName?: string
  triggerVariant?: "default" | "outline"
  triggerSize?: "default" | "sm"
  triggerLabel?: string
  /** Controlled open (e.g. opened from Update forecast). Pair with `onOpenChange` and usually `hideTrigger`. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Omit the default + trigger; parent opens via `open` / `openSignal`. */
  hideTrigger?: boolean
}

function CustomerSelectWithOther({
  value,
  onValueChange,
  planned_customers = [],
  customers,
}: {
  value: string
  onValueChange: (value: string, isOther: boolean) => void
  /** Customers already planned in this forecast — first-class options, never "Other". */
  planned_customers?: string[]
  /** The brand's accounts (`/customers`), loaded by the dialog so it can read their type too. */
  customers: Customer[]
}) {
  const [searchTerm, setSearchTerm] = useState("")
  // Latest committed value / typed term, readable from the deferred dismiss handler below
  // (state in that closure would be a render stale by the time the popover has closed).
  const value_ref = useRef(value)
  value_ref.current = value
  const search_ref = useRef("")

  // Already-planned forecast customers come first, then accounting customers,
  // then the static fallback — deduped case-insensitively.
  const accounting_names = customers.length > 0
    ? customers.map((customer: Customer) => customer.name)
    : FALLBACK_CUSTOMERS
  const seen = new Set<string>()
  const merged_names: { name: string; planned: boolean }[] = []
  for (const name of planned_customers) {
    const key = name.trim().toLowerCase()
    if (!key || seen.has(key)) continue
    seen.add(key)
    merged_names.push({ name, planned: true })
  }
  for (const name of accounting_names) {
    const key = name.trim().toLowerCase()
    if (!key || seen.has(key)) continue
    seen.add(key)
    merged_names.push({ name, planned: false })
  }
  const known_names = new Set(seen)

  // A free-typed customer that was created earlier still needs to render as the selection.
  const value_is_known = !value || known_names.has(value.trim().toLowerCase())

  // Typing a name nobody has creates it right from the list — no more picking "Other"
  // and re-typing into a second field (7-Eleven should be one keystroke away).
  const create_candidate = searchTerm.trim()
  const can_create =
    create_candidate.length > 0 && !known_names.has(create_candidate.toLowerCase())

  const options: SearchableSelectOption[] = [
    ...(!value_is_known ? [{ value, label: `${value} — new customer` }] : []),
    ...(can_create && create_candidate !== value
      ? [{ value: `__create__${create_candidate}`, label: `Create "${create_candidate}"` }]
      : []),
    // Planned customers sit under their own heading with a green tag: the old "— in this
    // forecast" suffix read as a restriction ("can I still use that customer?"); it is
    // information, and any of them can take another source of sales.
    ...merged_names.map(({ name, planned }) => ({
      value: name,
      label: name,
      group: planned ? "Already in this forecast (you can add more to them)" : "Customers",
      ...(planned ? { badge: "In this forecast" } : {}),
    })),
  ]

  function handleSelect(selectedValue: string) {
    setSearchTerm("")
    search_ref.current = ""
    if (selectedValue.startsWith("__create__")) {
      onValueChange(selectedValue.slice("__create__".length), true)
      return
    }
    const planned_or_accounting = known_names.has(selectedValue.trim().toLowerCase())
    onValueChange(selectedValue, !planned_or_accounting)
  }

  function handleSearchChange(term: string) {
    setSearchTerm(term)
    search_ref.current = term
  }

  // Dismissing the popover with a typed new name and no selection commits the name anyway —
  // the old free-text flow committed on every keystroke, and losing "7-Eleven" to a click-away
  // just re-raises the required-customer validation the user thinks they satisfied. Deferred a
  // tick so a Create/normal selection made in the same click lands first (it clears the term).
  function handleOpenChange(open: boolean) {
    if (open) return
    setTimeout(() => {
      const candidate = search_ref.current.trim()
      if (candidate && !value_ref.current && !known_names.has(candidate.toLowerCase())) {
        onValueChange(candidate, true)
      }
      setSearchTerm("")
      search_ref.current = ""
    }, 0)
  }

  return (
    <SearchableSelect
      options={options}
      value={value}
      onValueChange={handleSelect}
      onSearchChange={handleSearchChange}
      onOpenChange={handleOpenChange}
      placeholder="Pick or type a customer"
      searchPlaceholder="Search or type a new customer..."
      emptyMessage="Keep typing to create a new customer."
    />
  )
}

export function CreateSalesSourceDialog({
  onSave,
  periods,
  existing_hierarchy_roots = [],
  initialData,
  initialCustomerKind = "consumption",
  openSignal,
  triggerClassName,
  triggerVariant = "default",
  triggerSize = "default",
  triggerLabel = "Create Sales Source",
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: CreateSalesSourceDialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? Boolean(controlledOpen) : uncontrolledOpen
  const [collision_open, set_collision_open] = useState(false)
  const [collision_title, set_collision_title] = useState("")
  const [collision_body, set_collision_body] = useState("")
  const [product_lines, set_product_lines] = useState<ProductLine[]>([])

  useAsyncEffect(async () => {
    const res = await listProductLines()
    if (res.ok) {
      set_product_lines(res.value.data)
    }
  }, [])

  const weekly = useMemo(() => periods_are_weekly(periods), [periods])
  const period_span = weekly ? "week" : "month"

  const catalog_line_name_set = useMemo(
    () => new Set(product_lines.map((l: ProductLine) => l.name)),
    [product_lines]
  )

  /**
   * Customer/retailer names already planned in this forecast. Distributor nodes are left out: a
   * distributor split under Whole Foods puts a UNFI node in the tree, which is not UNFI planned
   * as a customer (it showed "UNFI — in this forecast" on a plan with no UNFI source).
   */
  const planned_customers = useMemo(() => {
    const names: string[] = []
    function walk(nodes: SalesHierarchyNode[]): void {
      for (const node of nodes) {
        const level = String(node.level)
        if (level === "customer" || level === "retailer") {
          names.push(node.name)
        }
        const children = (node as { children?: SalesHierarchyNode[] }).children
        if (Array.isArray(children)) walk(children)
      }
    }
    walk(existing_hierarchy_roots)
    return names.sort((a: string, b: string) => a.localeCompare(b))
  }, [existing_hierarchy_roots])

  function getDefaultStartMonth(): string {
    return default_start_period_key(periods, new Date())
  }

  function emptyFormData(): CreateSalesSourceFormData {
    return {
    classOfTrade: '',
    customer: '',
    routeToMarket: '',
    customerIsOther: false,
    planningBySku: false,
    isOpportunity: false,
    opportunityName: '',
    opportunityLikelihood: 50,
    seasonalityEnabled: false,
    seasonalityFactorsPct: [...FLAT_SEASONALITY_PCT],
    rtmDistributors: [],
    growthExpectations: []
    }
  }

  const [formData, setFormData] = useState<CreateSalesSourceFormData>(emptyFormData())
  /** Consumption = the retailer whose shelves sell it; Ship-to = the account we invoice. */
  const [customerKind, setCustomerKind] = useState<"consumption" | "ship_to">(initialCustomerKind)
  /** What the picked account's own record said about its kind — shown under the toggle, and cleared when the planner overrides it. */
  const [kind_hint, set_kind_hint] = useState<CustomerKindHint | null>(null)
  /** Per-store levers for a retailer; one account-growth rate for a distributor (item 12). */
  const growth_inputs = growth_inputs_for_customer(kind_hint)
  const [customers, setCustomers] = useState<Customer[]>([])

  useAsyncEffect(async () => {
    try {
      // The route is `/customers` (server.rs); `/accounting/customers` 404'd on every open, so the
      // picker fell back to the static list and never showed a brand's own accounts.
      const data = await api_fetch_throws<Customer[]>("/customers", { use_cache: true })
      setCustomers(data)
    } catch (error) {
      console.error("Failed to load customers:", error)
      // Use fallback list if API fails
      setCustomers([])
    }
  }, [])

  /**
   * The months this source would put on the grid, as the form stands. Generated the way the
   * create handler generates it (same basis, same SKU pricing), so what the preview shows is
   * what Save adds.
   */
  const preview: SalesSourcePreview | null = useMemo(
    () =>
      preview_sales_source(formData, periods, {
        quantity_basis: plan_quantity_basis(existing_hierarchy_roots),
        sku_reference: (stable_key: string | undefined) => sku_plan_reference(existing_hierarchy_roots, stable_key),
      }),
    [formData, periods, existing_hierarchy_roots]
  )
  // Optional in the type (other form-data literal sites omit them); always present in dialog state.
  const seasonality_factors = formData.seasonalityFactorsPct ?? FLAT_SEASONALITY_PCT
  const rtm_split_warning = useMemo(() => {
    const rows = (formData.rtmDistributors || []).filter((r: RtmDistributorSplitRow) => r.name.trim())
    if (rows.length === 0) return null
    // Over 100% or a repeated distributor is refused at save; say so while it is being typed.
    const error = distributor_split_error(rows)
    if (error) return error
    const total = rows.reduce((sum: number, r: RtmDistributorSplitRow) => sum + (Number(r.allocation_pct) || 0), 0)
    if (Math.abs(total - 100) > 0.5) {
      return `Split adds to ${Math.round(total)}% (expected 100%)`
    }
    return null
  }, [formData.rtmDistributors])

  useEffect(() => {
    if (openSignal === undefined || openSignal === null) return
    const defaultStartMonth = getDefaultStartMonth()
    setCustomerKind(initialCustomerKind)
    set_kind_hint(null)
    setFormData({
      ...emptyFormData(),
      ...initialData,
      growthExpectations: (initialData?.growthExpectations || []).map((exp: GrowthExpectation) => ({
        ...exp,
        startMonth: exp.startMonth || defaultStartMonth,
        // Row-menu prefills carry free-text SKU values; keep those fields visible so
        // the user can see and correct them. A prefill whose source row already had a
        // catalog identity (sku:<upc> stable key / catalog metadata) is NOT "other".
        skuIsOther:
          exp.skuIsOther ??
          Boolean((exp.productSku?.trim() || exp.skuName?.trim()) && !(exp.productId || exp.productUpc)),
      })),
    })
    if (isControlled) {
      onOpenChange?.(true)
    } else {
      setUncontrolledOpen(true)
    }
  }, [openSignal])

  // Auto-add growth expectation when customer is selected.
  useEffect(() => {
    const { customer, growthExpectations } = formData
    if (customer && growthExpectations.length === 0) {
      addGrowthExpectation()
    }
  }, [formData.customer])

  // Free-typed SKU text resolves against the catalog — which also matches the product
  // aliases from the Forecast Mappings page — so an already-mapped name silently picks
  // up its catalog identity instead of warning that actuals can never land.
  const unresolved_sku_signature = formData.growthExpectations
    .filter(
      (exp: GrowthExpectation) =>
        exp.skuIsOther && !(exp.productId || exp.productUpc) && (exp.skuName?.trim() || exp.productSku?.trim())
    )
    .map((exp: GrowthExpectation) => `${exp.id}:${exp.skuName ?? ""}:${exp.productSku ?? ""}`)
    .join("|")
  useEffect(() => {
    if (!unresolved_sku_signature) return
    const timer = setTimeout(() => {
      void (async () => {
        for (const exp of formData.growthExpectations) {
          if (!exp.skuIsOther || exp.productId || exp.productUpc) continue
          const query = (exp.skuName?.trim() || exp.productSku?.trim()) ?? ""
          if (!query) continue
          const res = await searchProducts(query, 10)
          if (!res.ok) continue
          const match = exact_catalog_match(query, res.value.data)
          if (!match) continue
          setFormData((prev) => ({
            ...prev,
            growthExpectations: prev.growthExpectations.map((row: GrowthExpectation) =>
              row.id === exp.id && !(row.productId || row.productUpc)
                ? {
                    ...row,
                    productId: match.id,
                    productUpc: product_upc(match),
                    unitsPerCase: match.units_per_case,
                    productSku: row.productSku?.trim() ? row.productSku : match.sku || "",
                    ...(match.list_price != null && match.list_price > 0 && !(row.costPerCase > 0)
                      ? { costPerCase: Math.round(match.list_price * 100) / 100 }
                      : {}),
                  }
                : row
            ),
          }))
        }
      })()
    }, 450)
    return () => clearTimeout(timer)
  }, [unresolved_sku_signature])

  // A controlled open (the New menu) carries its own customer kind; the openSignal path above
  // covers the row-menu prefills, which always reopen with a fresh signal.
  useEffect(() => {
    if (isOpen) setCustomerKind(initialCustomerKind)
  }, [isOpen, initialCustomerKind])

  // Default start month whenever periods load, dialog opens, or a row has an invalid/empty key.
  // Radix Select must not receive "" for `value` — use a real period_key or leave uncontrolled (undefined).
  useEffect(() => {
    if (periods.length === 0) return
    const defaultStartMonth = getDefaultStartMonth()
    if (!defaultStartMonth) return
    setFormData((prev: CreateSalesSourceFormData) => ({
      ...prev,
      growthExpectations: prev.growthExpectations.map((exp: GrowthExpectation) => {
        if (growth_start_month_is_valid(exp.startMonth, periods)) {
          return { ...exp, startMonth: (exp.startMonth ?? "").trim() }
        }
        return { ...exp, startMonth: defaultStartMonth }
      }),
    }))
  }, [periods, isOpen])

  function handleCustomerChange(customer: string, isOther: boolean) {
    // The account's own record decides how it buys: a distributor or direct account is its own
    // ship-to root (no split to type, and no DSD offered to Kroger), a retailer under a
    // distributor gets that distributor as its split. A free-typed name says nothing, so the
    // toggle and split stay as they are.
    const hint = isOther ? undefined : customer_kind_hint(customers, customer)
    set_kind_hint(hint ?? null)
    if (hint) setCustomerKind(hint.kind)
    setFormData(prev => ({
      ...prev,
      customer,
      customerIsOther: isOther,
      ...(hint?.route_to_market && !prev.routeToMarket ? { routeToMarket: hint.route_to_market } : {}),
      ...(hint
        ? {
            rtmDistributors: hint.distributor
              ? [{ name: hint.distributor, allocation_pct: 100 }]
              : hint.kind === "ship_to"
                ? []
                : prev.rtmDistributors,
          }
        : {}),
      opportunityName: prev.isOpportunity && !prev.opportunityName ? `${customer} opportunity` : prev.opportunityName,
      // Clear growth expectations when customer changes
      growthExpectations: []
    }))
  }

  function addGrowthExpectation() {
    const defaultStartMonth = getDefaultStartMonth()

    const newExpectation: GrowthExpectation = {
      id: next_growth_expectation_id(),
      // Chain name starts empty (attach directly under the customer unless the user names a chain).
      name: '',
      initialDoors: 0,
      startMonth: defaultStartMonth,
      // A distributor has no door growth to plan (see `growth_inputs_for_customer`).
      storeCountGrowthMultiplier: growth_inputs === "account" ? 0 : 0.02,
      productLineName: '',
      initialCasesPerMonth: 0,
      costPerCase: 0,
      sameStoreSalesMultiplier: 0.03,
      productSku: '',
      skuName: ''
    }
    setFormData(prev => ({
      ...prev,
      growthExpectations: [...prev.growthExpectations, newExpectation]
    }))
  }

  function duplicateGrowthExpectation(existingId: string) {
    const existing = formData.growthExpectations.find(exp => exp.id === existingId)
    if (!existing) return

    const newExpectation: GrowthExpectation = {
      id: next_growth_expectation_id(),
      name: existing.name,
      initialDoors: existing.initialDoors,
      startMonth: existing.startMonth || getDefaultStartMonth(),
      endMonth: existing.endMonth,
      storeCountGrowthMultiplier: existing.storeCountGrowthMultiplier,
      productLineName: existing.productLineName,
      initialCasesPerMonth: existing.initialCasesPerMonth,
      costPerCase: existing.costPerCase,
      sameStoreSalesMultiplier: existing.sameStoreSalesMultiplier,
      productSku: '', // Clear SKU fields for new entry
      skuName: ''
    }
    
    setFormData(prev => ({
      ...prev,
      growthExpectations: [...prev.growthExpectations, newExpectation]
    }))
  }

  function updateGrowthExpectation(id: string, field: keyof GrowthExpectation, value: any) {
    setFormData(prev => ({
      ...prev,
      growthExpectations: prev.growthExpectations.map(exp =>
        exp.id === id ? { ...exp, [field]: value } : exp
      )
    }))
  }

  /** Manual SKU-text edits invalidate any previously resolved catalog identity. */
  function updateGrowthExpectationSkuText(id: string, field: "productSku" | "skuName", value: string) {
    setFormData(prev => ({
      ...prev,
      growthExpectations: prev.growthExpectations.map(exp =>
        exp.id === id
          ? { ...exp, [field]: value, productId: undefined, productUpc: undefined, unitsPerCase: undefined }
          : exp
      )
    }))
  }

  function removeGrowthExpectation(id: string) {
    setFormData(prev => ({
      ...prev,
      growthExpectations: prev.growthExpectations.filter(exp => exp.id !== id)
    }))
  }


  function run_save_after_validation(): void {
    const shouldPlanBySku = formData.growthExpectations.some(
      (exp: GrowthExpectation) => Boolean(exp.productSku?.trim()) || Boolean(exp.skuName?.trim())
    )
    onSave({
      ...formData,
      planningBySku: shouldPlanBySku,
      // A ship-to account IS its own route to market: a 100% self split makes it
      // group as its own root in the Ship-to view instead of the unmapped bucket.
      ...(customerKind === "ship_to" && formData.customer.trim()
        ? { rtmDistributors: [{ name: formData.customer.trim(), allocation_pct: 100 }] }
        : {}),
    })
    if (isControlled) {
      onOpenChange?.(false)
    } else {
      setUncontrolledOpen(false)
    }
    setFormData(emptyFormData())
  }

  function handleSave() {
    const err = sales_source_validation_error(formData, periods)
    if (err) {
      alert(err)
      return
    }

    const analysis = analyzeSalesSourceCollision(existing_hierarchy_roots, formData, periods)
    if (analysis.needs_confirmation) {
      set_collision_title(analysis.title)
      set_collision_body(analysis.body)
      set_collision_open(true)
      return
    }

    run_save_after_validation()
  }

  function handle_collision_continue(): void {
    set_collision_open(false)
    const err = sales_source_validation_error(formData, periods)
    if (err) {
      alert(err)
      return
    }
    run_save_after_validation()
  }

  function handleDialogOpenChange(next: boolean) {
    if (isControlled) {
      onOpenChange?.(next)
    } else {
      setUncontrolledOpen(next)
    }
  }

  return (
    <>
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      {!hideTrigger ? (
        <DialogTrigger asChild>
          <Button variant={triggerVariant} size={triggerSize} className={`flex items-center gap-2 ${triggerClassName || ""}`}>
            <Plus className="h-4 w-4" />
            {triggerLabel}
          </Button>
        </DialogTrigger>
      ) : null}
      <DialogContent 
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => {
          // Prevent closing when clicking on popover content or input fields
          const target = e.target as HTMLElement
          if (
            target.closest('[data-radix-popper-content-wrapper]') ||
            target.closest('[role="combobox"]') ||
            target.closest('input[type="text"]')
          ) {
            e.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Create a New Source of Sales</DialogTitle>
          <p className="text-sm text-muted-foreground">
            <span className="text-red-600">*</span> Required fields. Fields labeled “Optional” can be left blank.
          </p>
        </DialogHeader>
        
        <div className="space-y-8">
          {/* Section 1: Create a new source of sales */}
          <div className="space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Customer type</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerKind("consumption")
                      set_kind_hint(null)
                    }}
                    className={`rounded-md border p-2.5 text-left transition-colors ${
                      customerKind === "consumption"
                        ? "border-blue-400 bg-blue-50/60 ring-1 ring-blue-200"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className="block text-sm font-medium text-gray-900">Consumption customer</span>
                    <span className="mt-0.5 block text-xs leading-snug text-gray-500">
                      The retailer or planning group whose shelves sell the product (Whole Foods, Kroger…). Set the
                      distributor split below to drive the Ship-to view.
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerKind("ship_to")
                      set_kind_hint(null)
                      // A ship-to account is its own route: a split typed earlier must not ride
                      // along (it was hidden, not cleared, and came back if the kind was toggled
                      // again), so it goes now.
                      setFormData((prev) => ({ ...prev, rtmDistributors: [] }))
                    }}
                    className={`rounded-md border p-2.5 text-left transition-colors ${
                      customerKind === "ship_to"
                        ? "border-blue-400 bg-blue-50/60 ring-1 ring-blue-200"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className="block text-sm font-medium text-gray-900">Ship-to customer</span>
                    <span className="mt-0.5 block text-xs leading-snug text-gray-500">
                      The account you invoice directly (UNFI, KeHE, a direct chain). Groups as its own root in the
                      Ship-to view — no distributor split needed.
                    </span>
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer">
                  Customer <span className="text-red-600">*</span>
                </Label>
                <CustomerSelectWithOther
                  value={formData.customer}
                  onValueChange={(value, isOther) => handleCustomerChange(value, isOther)}
                  planned_customers={planned_customers}
                  customers={customers}
                />
                {kind_hint?.reason ? (
                  <p className="flex items-start gap-1.5 text-xs text-green-800">
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{kind_hint.reason}</span>
                  </p>
                ) : null}
              </div>

              {/* A direct account (Kroger) is its own route to market, so the field only shows for
                  a customer that reaches the shelf through a distributor, or one we know nothing about. */}
              {customer_routes_through_distributor(kind_hint) ? (
                <div className="space-y-2">
                  <Label htmlFor="route-to-market">Route to market (optional)</Label>
                  <Select
                    value={formData.routeToMarket}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, routeToMarket: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Optional — select if it applies" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROUTE_TO_MARKET_OPTIONS.map(option => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              <div className={customerKind === "ship_to" ? "hidden" : "space-y-2"}>
                <Label>Distributor split (optional)</Label>
                <p className="text-xs text-gray-500">
                  Which distributor(s) this customer buys through, e.g. UNFI 60% / KeHE 40%. Drives the
                  Ship-to view; leave empty for direct accounts or when unknown.
                </p>
                {(formData.rtmDistributors || []).map((row: RtmDistributorSplitRow, rowIndex: number) => (
                  <div key={rowIndex} className="flex items-center gap-2">
                    <Input
                      value={row.name}
                      onChange={(e) => {
                        const name = (e.target as HTMLInputElement).value
                        setFormData((prev) => ({
                          ...prev,
                          rtmDistributors: (prev.rtmDistributors || []).map(
                            (r: RtmDistributorSplitRow, i: number) => (i === rowIndex ? { ...r, name } : r)
                          ),
                        }))
                      }}
                      placeholder="Distributor name (e.g. UNFI)"
                      className="flex-1"
                    />
                    <div className="flex w-24 shrink-0 items-center gap-1">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={row.allocation_pct}
                        onChange={(e) => {
                          const allocation_pct = parseFloat((e.target as HTMLInputElement).value) || 0
                          setFormData((prev) => ({
                            ...prev,
                            rtmDistributors: (prev.rtmDistributors || []).map(
                              (r: RtmDistributorSplitRow, i: number) =>
                                i === rowIndex ? { ...r, allocation_pct } : r
                            ),
                          }))
                        }}
                      />
                      <span className="text-xs text-gray-500">%</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-gray-500"
                      aria-label="Remove distributor row"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          rtmDistributors: (prev.rtmDistributors || []).filter(
                            (_: RtmDistributorSplitRow, i: number) => i !== rowIndex
                          ),
                        }))
                      }
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setFormData((prev) => {
                        const rows = prev.rtmDistributors || []
                        const used = rows.reduce(
                          (sum: number, r: RtmDistributorSplitRow) => sum + (Number(r.allocation_pct) || 0),
                          0
                        )
                        return {
                          ...prev,
                          rtmDistributors: [
                            ...rows,
                            { name: "", allocation_pct: Math.max(0, 100 - used) },
                          ],
                        }
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add distributor
                  </Button>
                  {rtm_split_warning ? (
                    <span className="text-xs text-amber-600">{rtm_split_warning}</span>
                  ) : null}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="class-of-trade">Class of trade (optional)</Label>
                <p className="text-xs text-gray-500">
                  Leave blank to attach this source directly under the customer without a class-of-trade folder.
                </p>
                <Select 
                  value={formData.classOfTrade || "__none__"} 
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, classOfTrade: value === "__none__" ? "" : value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Optional — none means customer-level root" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No class of trade</SelectItem>
                    {CLASS_OF_TRADE_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="is-opportunity"
                    checked={formData.isOpportunity}
                    onCheckedChange={(checked: boolean) => setFormData(prev => ({
                      ...prev,
                      isOpportunity: checked,
                      opportunityName: checked && !prev.opportunityName ? `${prev.customer || "Sales"} opportunity` : prev.opportunityName,
                    }))}
                  />
                  <div className="space-y-3">
                    <Label htmlFor="is-opportunity">Model as sales opportunity</Label>
                    {formData.isOpportunity && (
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Opportunity name (optional)</Label>
                          <Input
                            value={formData.opportunityName}
                            onChange={(e) => setFormData(prev => ({ ...prev, opportunityName: (e.target as HTMLInputElement).value }))}
                            placeholder="e.g., Kroger national launch"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Likelihood % (optional)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.opportunityLikelihood}
                            onChange={(e) => setFormData(prev => ({ ...prev, opportunityLikelihood: parseFloat((e.target as HTMLInputElement).value) || 0 }))}
                            placeholder="50"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-sky-200 bg-sky-50 p-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="seasonality-enabled"
                    checked={formData.seasonalityEnabled}
                    onCheckedChange={(checked: boolean) => setFormData(prev => ({ ...prev, seasonalityEnabled: checked }))}
                  />
                  <div className="min-w-0 flex-1 space-y-3">
                    <div>
                      <Label htmlFor="seasonality-enabled">Apply seasonality factors</Label>
                      <p className="text-xs text-gray-500">
                        Shape each month as a percent of baseline volume (100 = flat). Applies to cases and dollars, not door count.
                      </p>
                    </div>
                    {formData.seasonalityEnabled && (
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          {SEASONALITY_PRESETS.map((preset) => {
                            const active = preset.factors_pct.every(
                              (pct: number, i: number) => pct === seasonality_factors[i]
                            )
                            return (
                              <Button
                                key={preset.label}
                                type="button"
                                size="sm"
                                variant={active ? "default" : "outline"}
                                className="h-7 px-2 text-xs"
                                onClick={() => setFormData(prev => ({ ...prev, seasonalityFactorsPct: [...preset.factors_pct] }))}
                              >
                                {preset.label}
                              </Button>
                            )
                          })}
                        </div>
                        <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                          {SEASONALITY_MONTH_LABELS.map((month: string, index: number) => (
                            <div key={month} className="space-y-1">
                              <Label className="text-[11px] text-gray-600">{month}</Label>
                              <div className="relative">
                                <Input
                                  type="number"
                                  min="0"
                                  max="1000"
                                  step="5"
                                  className={`h-8 pr-6 text-sm ${seasonality_factors[index] !== 100 ? "border-sky-400 bg-white" : ""}`}
                                  value={seasonality_factors[index]}
                                  onChange={(e) => {
                                    const value = parseFloat((e.target as HTMLInputElement).value)
                                    setFormData(prev => {
                                      const next = [...(prev.seasonalityFactorsPct ?? FLAT_SEASONALITY_PCT)]
                                      next[index] = Number.isFinite(value) ? value : 100
                                      return { ...prev, seasonalityFactorsPct: next }
                                    })
                                  }}
                                />
                                <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-gray-400">%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Tiny profile preview so the shape is visible at a glance. */}
                        <div className="flex h-8 items-end gap-[3px]" aria-hidden="true">
                          {seasonality_factors.map((pct: number, index: number) => (
                            <div
                              key={index}
                              className={`w-3 rounded-sm ${pct === 100 ? "bg-gray-300" : pct < 100 ? "bg-sky-400" : "bg-emerald-400"}`}
                              style={{ height: `${Math.max(8, Math.min(100, (Math.max(0, pct) / 150) * 100))}%` }}
                              title={`${SEASONALITY_MONTH_LABELS[index]}: ${pct}%`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                SKU fields are optional. If provided, this row is modeled at SKU level automatically.
              </p>
            </div>
          </div>

          {/* Section 2: Growth Expectations */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Growth expectations</h3>
                <p className="text-xs text-gray-500">
                  Product line, initial doors, cases, and cost are required per row. Chain name and multipliers are optional where noted.
                </p>
              </div>
              <Button onClick={addGrowthExpectation}>
                <Plus className="h-4 w-4 mr-1" />
                Add Growth Expectation
              </Button>
            </div>

            {formData.growthExpectations.map((expectation, index) => {
              // What "a case" of this SKU is, read off the plan's own rows for it.
              const case_hint = case_basis_hint({
                reference: sku_plan_reference(existing_hierarchy_roots, sku_stable_key_for_expectation(expectation)),
                catalog_units_per_case: expectation.unitsPerCase,
                cases_per_store: cases_per_store_input_value(expectation.initialCasesPerMonth, weekly),
                price_per_case: expectation.costPerCase,
                span: period_span,
              })
              return (
              <div key={expectation.id} className="border rounded p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Growth Expectation {index + 1}</h4>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => duplicateGrowthExpectation(expectation.id)} 
                      variant="outline"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Duplicate Row
                    </Button>
                    <Button 
                      onClick={() => removeGrowthExpectation(expectation.id)} 
                      variant="ghost"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Chain name (optional)</Label>
                    <Input
                      value={expectation.name}
                      onChange={(e) => updateGrowthExpectation(expectation.id, 'name', (e.target as HTMLInputElement).value)}
                      placeholder="e.g., Whole Foods Southeast"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Product line <span className="text-red-600">*</span>
                    </Label>
                    <Select
                      value={
                        !expectation.productLineName
                          ? "__unset__"
                          : catalog_line_name_set.has(expectation.productLineName)
                            ? expectation.productLineName
                            : "__custom__"
                      }
                      onValueChange={(value) => {
                        if (value === "__unset__") {
                          updateGrowthExpectation(expectation.id, "productLineName", "")
                        } else if (value === "__custom__") {
                          updateGrowthExpectation(expectation.id, "productLineName", "")
                        } else {
                          updateGrowthExpectation(expectation.id, "productLineName", value)
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="From catalog or custom" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unset__">Select catalog line…</SelectItem>
                        {product_lines.map((line: ProductLine) => (
                          <SelectItem key={line.id} value={line.name}>
                            {line.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="__custom__">Custom name…</SelectItem>
                      </SelectContent>
                    </Select>
                    {(!expectation.productLineName || !catalog_line_name_set.has(expectation.productLineName)) ? (
                      <Input
                        value={expectation.productLineName}
                        onChange={(e) =>
                          updateGrowthExpectation(expectation.id, "productLineName", (e.target as HTMLInputElement).value)
                        }
                        placeholder="Enter product line name"
                      />
                    ) : null}
                  </div>
                  <>
                    <div className="space-y-2 sm:col-span-2">
                      <p className="text-xs text-gray-600">SKU select (optional, type to search)</p>
                      {expectation.skuName && !expectation.skuIsOther ? (
                        /* A picked SKU replaces the search box — an empty "Search catalog SKU…"
                           field next to a tiny caption read like it still needed filling in. */
                        <div className="flex items-center justify-between gap-3 rounded-md border border-emerald-300 bg-emerald-50/70 px-3 py-2.5">
                          <div className="flex min-w-0 items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-gray-900">{expectation.skuName}</p>
                              <p className="text-[11px] text-gray-600">
                                {expectation.productUpc ? `UPC ${expectation.productUpc}` : expectation.productSku || "Catalog SKU"}
                                {expectation.unitsPerCase ? ` · ${expectation.unitsPerCase}/case` : ""}
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="shrink-0"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                growthExpectations: prev.growthExpectations.map((exp) =>
                                  exp.id === expectation.id
                                    ? {
                                        ...exp,
                                        skuName: "",
                                        productSku: "",
                                        productId: undefined,
                                        productUpc: undefined,
                                        unitsPerCase: undefined,
                                        skuIsOther: false,
                                      }
                                    : exp
                                ),
                              }))
                            }
                          >
                            Change
                          </Button>
                        </div>
                      ) : (
                      <SkuSearchSelect
                        product_line={catalog_line_name_set.has(expectation.productLineName) ? expectation.productLineName : undefined}
                        onValueChange={(next) => {
                          setFormData((prev) => ({
                            ...prev,
                            growthExpectations: prev.growthExpectations.map((exp) =>
                              exp.id === expectation.id
                                ? {
                                    ...exp,
                                    productSku: next.product_sku,
                                    skuName: next.sku_name,
                                    skuIsOther: false,
                                    // Carry the catalog identity so the node gets a
                                    // resolvable `sku:<upc>` stable key.
                                    productId: next.product_id,
                                    productUpc: next.product_upc,
                                    unitsPerCase: next.units_per_case,
                                    // Keep the row's product line in step with the catalog
                                    // when the user hasn't typed one.
                                    ...(next.product_line && !exp.productLineName.trim()
                                      ? { productLineName: next.product_line }
                                      : {}),
                                    // Catalog price per case is the source of truth when set;
                                    // cents-rounded so no float dust reaches the input.
                                    ...(next.list_price != null && next.list_price > 0
                                      ? { costPerCase: Math.round(next.list_price * 100) / 100 }
                                      : {}),
                                  }
                                : exp
                            ),
                          }))
                        }}
                        onOther={() => {
                          setFormData((prev) => ({
                            ...prev,
                            growthExpectations: prev.growthExpectations.map((exp) =>
                              exp.id === expectation.id
                                ? {
                                    ...exp,
                                    skuIsOther: true,
                                    // Manual entry: drop any previously attached catalog identity.
                                    productId: undefined,
                                    productUpc: undefined,
                                    unitsPerCase: undefined,
                                  }
                                : exp
                            ),
                          }))
                        }}
                      />
                      )}
                    </div>
                    {expectation.skuIsOther ? (
                      <>
                        <div className="space-y-2">
                          <Label>Product SKU (optional)</Label>
                          <Input
                            value={expectation.productSku || ''}
                            onChange={(e) => updateGrowthExpectationSkuText(expectation.id, 'productSku', (e.target as HTMLInputElement).value)}
                            placeholder="e.g., SKU-12345"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>SKU name</Label>
                          <Input
                            value={expectation.skuName || ''}
                            onChange={(e) => updateGrowthExpectationSkuText(expectation.id, 'skuName', (e.target as HTMLInputElement).value)}
                            placeholder="e.g., Organic Granola Bar - Chocolate"
                          />
                          {expectation.productId || expectation.productUpc ? (
                            <p className="text-[11px] text-emerald-700">
                              Matches a catalog product{expectation.productUpc ? ` (UPC ${expectation.productUpc})` : ""} —
                              QuickBooks actuals can land on this row.
                            </p>
                          ) : expectation.skuName?.trim() || expectation.productSku?.trim() ? (
                            <p className="text-[11px] text-amber-700">
                              No catalog or mapping match — this row cannot receive QuickBooks actuals until the
                              product exists in the catalog (or is mapped on the Forecast Mappings page).
                            </p>
                          ) : null}
                        </div>
                      </>
                    ) : null}
                  </>
                  <div className="space-y-2">
                    <Label>
                      Initial Doors <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      value={expectation.initialDoors}
                      onChange={(e) => updateGrowthExpectation(expectation.id, 'initialDoors', parseInt((e.target as HTMLInputElement).value) || 0)}
                      placeholder="Number of stores"
                    />
                  </div>
                  {growth_inputs === "per_store" ? (
                    <div className="space-y-2">
                      <Label>Store count growth (% per year)</Label>
                      <DecimalInput
                        value={Math.round(expectation.storeCountGrowthMultiplier * 100 * 1e6) / 1e6}
                        onValueChange={(pct: number) => updateGrowthExpectation(expectation.id, 'storeCountGrowthMultiplier', pct / 100)}
                        placeholder="2.0"
                      />
                      <p className="text-[11px] text-gray-500">
                        Doors added over twelve months, spread month by month. Same-store sales never add doors.
                      </p>
                    </div>
                  ) : null}
                  <div className="space-y-2">
                    <Label>
                      Cases per store per {period_span} <span className="text-red-600">*</span>
                    </Label>
                    <DecimalInput
                      value={cases_per_store_input_value(expectation.initialCasesPerMonth, weekly)}
                      onValueChange={(cases: number) =>
                        updateGrowthExpectation(
                          expectation.id,
                          'initialCasesPerMonth',
                          cases_per_month_from_input(cases, weekly)
                        )
                      }
                      placeholder={`Cases per store per ${period_span}`}
                    />
                    <p className="text-[11px] text-gray-500">
                      One store's rate, not the total across all doors.
                    </p>
                    {case_hint ? (
                      <p className="text-[11px] font-medium text-gray-700">{case_hint.text}</p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    {/* A distributor's volume is the sum of the accounts it serves: one growth rate for
                        the account, no door levers. It rides in the same field the generator already
                        reads, with store-count growth held at 0. */}
                    <Label>{growth_inputs === "account" ? "Account growth (% per year)" : "Same-store sales growth (% per year)"}</Label>
                    <DecimalInput
                      value={Math.round(expectation.sameStoreSalesMultiplier * 100 * 1e6) / 1e6}
                      onValueChange={(pct: number) => updateGrowthExpectation(expectation.id, 'sameStoreSalesMultiplier', pct / 100)}
                      placeholder="3.0"
                    />
                    <p className="text-[11px] text-gray-500">
                      {growth_inputs === "account"
                        ? `How much more the account buys after twelve months — 10% lifts the rate 10% by next ${period_span === "week" ? "year" : "January"}, not every ${period_span}.`
                        : `How much more each store sells after twelve months — 10% lifts a store's rate 10% by next ${period_span === "week" ? "year" : "January"}, not every ${period_span}.`}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Price per case ($) <span className="text-red-600">*</span>
                    </Label>
                    <DecimalInput
                      value={expectation.costPerCase}
                      onValueChange={(price: number) => updateGrowthExpectation(expectation.id, 'costPerCase', price)}
                      placeholder="50.00"
                    />
                    {case_hint?.warning ? (
                      <p className="text-[11px] font-medium text-amber-700">{case_hint.warning}</p>
                    ) : null}
                    <SuggestedPriceCaption
                      customer={formData.customer || null}
                      product_id={expectation.productId}
                      current_price={expectation.costPerCase}
                      on_apply={(price: number) => updateGrowthExpectation(expectation.id, 'costPerCase', price)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Start {period_span} <span className="text-red-600">*</span>
                    </Label>
                    <Select
                      value={
                        growth_start_month_is_valid(expectation.startMonth, periods)
                          ? (expectation.startMonth ?? "").trim()
                          : undefined
                      }
                      onValueChange={(value: string) =>
                        updateGrowthExpectation(expectation.id, "startMonth", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={`Select start ${period_span}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {periods.map(period => (
                          <SelectItem key={period.period_key} value={period.period_key}>
                            {format_period_header(period)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>End {period_span} (optional)</Label>
                    <Select
                      value={
                        growth_start_month_is_valid(expectation.endMonth, periods)
                          ? (expectation.endMonth ?? "").trim()
                          : NO_END_PERIOD
                      }
                      onValueChange={(value: string) =>
                        updateGrowthExpectation(expectation.id, "endMonth", value === NO_END_PERIOD ? undefined : value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_END_PERIOD}>No end, runs through the plan</SelectItem>
                        {periods_from(periods, expectation.startMonth).map((period: TimePeriod) => (
                          <SelectItem key={period.period_key} value={period.period_key}>
                            {format_period_header(period)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-gray-500">
                      The last {period_span} with volume. Every {period_span} after it is set to zero.
                    </p>
                  </div>
                  {growth_inputs === "per_store" && growth_start_month_is_valid(expectation.startMonth, periods) ? (
                    <div className="space-y-2 md:col-span-2">
                      <Label>Doors by {period_span}</Label>
                      <DoorsByMonthEditor
                        initial_doors={expectation.initialDoors}
                        store_growth={expectation.storeCountGrowthMultiplier}
                        start_period={(expectation.startMonth ?? "").trim()}
                        periods={periods}
                        doors_by_period={expectation.doorsByPeriod ?? {}}
                        on_change={(next: Record<string, number>) => updateGrowthExpectation(expectation.id, "doorsByPeriod", next)}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            )})}
          </div>


          <SalesSourcePreviewPanel preview={preview} periods={periods} />

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => handleDialogOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Create Sales Source
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <AlertDialog open={collision_open} onOpenChange={set_collision_open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{collision_title}</AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-wrap text-sm text-gray-700">
            {collision_body}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
          <AlertDialogAction type="button" onClick={() => handle_collision_continue()}>
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  )
}


function preview_money(value: number): string {
  return value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 })
}

/**
 * The months a source adds, as the form stands — the planner used to have to save to see what
 * doors × rate × price came to, and a slip (10% a month instead of a year) only showed on the grid.
 */
export function SalesSourcePreviewPanel({ preview, periods }: { preview: SalesSourcePreview | null; periods: TimePeriod[] }) {
  const by_key = new Map(periods.map((period: TimePeriod) => [period.period_key, period]))
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3" data-testid="sales-source-preview">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">What this adds</h3>
        {preview ? (
          <p className="text-xs text-gray-600">
            <strong className="text-gray-900">{preview_money(preview.total_dollars)}</strong> ·{" "}
            {Math.round(preview.total_cases).toLocaleString()} cases ·{" "}
            {preview.opening_stores === preview.closing_stores
              ? `${preview.opening_stores.toLocaleString()} doors`
              : `${preview.opening_stores.toLocaleString()} → ${preview.closing_stores.toLocaleString()} doors`}
          </p>
        ) : null}
      </div>
      {preview ? (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500">
                <th className="py-1 pr-2 text-left font-medium">Period</th>
                {preview.periods.map((row: PreviewPeriod) => (
                  <th key={row.period_key} className="px-1 py-1 text-right font-medium whitespace-nowrap">
                    {by_key.get(row.period_key) ? format_period_header(by_key.get(row.period_key)!) : row.period_key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-gray-800">
              <tr>
                <td className="py-0.5 pr-2 text-gray-500">Doors</td>
                {preview.periods.map((row: PreviewPeriod) => (
                  <td key={row.period_key} className="px-1 py-0.5 text-right tabular-nums">{row.stores.toLocaleString()}</td>
                ))}
              </tr>
              <tr>
                <td className="py-0.5 pr-2 text-gray-500">Cases</td>
                {preview.periods.map((row: PreviewPeriod) => (
                  <td key={row.period_key} className="px-1 py-0.5 text-right tabular-nums">{Math.round(row.cases).toLocaleString()}</td>
                ))}
              </tr>
              <tr className="font-medium">
                <td className="py-0.5 pr-2 text-gray-500">Revenue</td>
                {preview.periods.map((row: PreviewPeriod) => (
                  <td key={row.period_key} className="px-1 py-0.5 text-right tabular-nums">{preview_money(row.dollars)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-1 text-xs text-gray-500">
          Pick a customer and fill in doors, cases per store and a price to see the months this source adds.
        </p>
      )}
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("next_growth_expectation_id", () => {
    it("never hands out the same id twice, even within one millisecond", () => {
      const a = next_growth_expectation_id(1000)
      const b = next_growth_expectation_id(1000)
      expect(a).not.toBe(b)
      expect(a.startsWith("growth_1000_")).toBe(true)
    })
  })

  describe("growth_start_month_is_valid", () => {
    const periods: TimePeriod[] = [
      { year: 2026, month: 1, period_key: "2026-01" },
      { year: 2026, month: 2, period_key: "2026-02" },
    ]

    it("rejects empty and whitespace", () => {
      expect(growth_start_month_is_valid("", periods)).toBe(false)
      expect(growth_start_month_is_valid("   ", periods)).toBe(false)
      expect(growth_start_month_is_valid(null, periods)).toBe(false)
    })

    it("rejects keys not in the period list", () => {
      expect(growth_start_month_is_valid("2025-06", periods)).toBe(false)
    })

    it("accepts a matching period_key", () => {
      expect(growth_start_month_is_valid("2026-02", periods)).toBe(true)
      expect(growth_start_month_is_valid(" 2026-02 ", periods)).toBe(true)
    })
  })

  describe("sales_source_validation_error", () => {
    const periods: TimePeriod[] = [{ year: 2026, month: 1, period_key: "2026-01" }]

    function valid_expectation(): GrowthExpectation {
      return {
        id: "growth_1",
        name: "",
        initialDoors: 10,
        startMonth: "2026-01",
        storeCountGrowthMultiplier: 0.02,
        productLineName: "Granola",
        initialCasesPerMonth: 5,
        costPerCase: 24,
        sameStoreSalesMultiplier: 0.03,
        productSku: "",
        skuName: "",
      }
    }

    function form_with(expectations: GrowthExpectation[]): CreateSalesSourceFormData {
      return {
        classOfTrade: "",
        customer: "UNFI",
        routeToMarket: "",
        customerIsOther: false,
        planningBySku: false,
        isOpportunity: false,
        opportunityName: "",
        opportunityLikelihood: 50,
        growthExpectations: expectations,
      }
    }

    it("passes with a complete row and an empty chain name", () => {
      expect(sales_source_validation_error(form_with([valid_expectation()]), periods)).toBeNull()
    })

    it("requires initial doors > 0", () => {
      const exp = { ...valid_expectation(), initialDoors: 0 }
      expect(sales_source_validation_error(form_with([exp]), periods)).toContain("initial doors")
    })

    it("requires a customer", () => {
      const form = { ...form_with([valid_expectation()]), customer: "" }
      expect(sales_source_validation_error(form, periods)).toContain("customer")
    })

    it("names the weekly fields on a weekly plan, and rejects an end before the start", () => {
      const weeks: TimePeriod[] = [
        { year: 2027, month: 2, period_key: "2027-W05", grain: "week" },
        { year: 2027, month: 2, period_key: "2027-W06", grain: "week" },
      ]
      const missing_rate = { ...valid_expectation(), startMonth: "2027-W06", initialCasesPerMonth: 0 }
      expect(sales_source_validation_error(form_with([missing_rate]), weeks)).toContain("cases per store per week")

      const backwards = { ...valid_expectation(), startMonth: "2027-W06", endMonth: "2027-W05" }
      expect(sales_source_validation_error(form_with([backwards]), weeks)).toBe("End week is before the start week.")

      const one_week = { ...valid_expectation(), startMonth: "2027-W06", endMonth: "2027-W06" }
      expect(sales_source_validation_error(form_with([one_week]), weeks)).toBeNull()
    })

    it("refuses a distributor split over 100% or naming one distributor twice", () => {
      const over = { ...form_with([valid_expectation()]), rtmDistributors: [{ name: "UNFI", allocation_pct: 70 }, { name: "KeHE", allocation_pct: 40 }] }
      expect(sales_source_validation_error(over, periods)).toContain("cannot exceed 100%")
      const twice = { ...form_with([valid_expectation()]), rtmDistributors: [{ name: "KeHE", allocation_pct: 50 }, { name: "kehe", allocation_pct: 50 }] }
      expect(sales_source_validation_error(twice, periods)).toContain("twice")
      const fine = { ...form_with([valid_expectation()]), rtmDistributors: [{ name: "UNFI", allocation_pct: 60 }, { name: "KeHE", allocation_pct: 40 }] }
      expect(sales_source_validation_error(fine, periods)).toBeNull()
    })
  })

  describe("cases per store on a weekly plan", () => {
    it("shows and takes the weekly rate while the form keeps the monthly one", () => {
      const stored = cases_per_month_from_input(3, true)
      expect(stored).toBeCloseTo(13.044, 3)
      expect(cases_per_store_input_value(stored, true)).toBe(3)
      // A fractional velocity survives the round trip as typed.
      expect(cases_per_store_input_value(cases_per_month_from_input(0.685, true), true)).toBe(0.685)
    })

    it("is the monthly rate, untouched, on a monthly plan", () => {
      expect(cases_per_month_from_input(13, false)).toBe(13)
      expect(cases_per_store_input_value(13, false)).toBe(13)
    })
  })

  describe("default_start_period_key", () => {
    it("starts a weekly plan in the current week, not the plan's first week", () => {
      const weeks: TimePeriod[] = ["2026-W01", "2026-W40", "2026-W41"].map((period_key: string) => ({
        year: 2026,
        month: 1,
        period_key,
        grain: "week",
      }))
      // Thursday 1 October 2026 is in ISO week 40.
      expect(default_start_period_key(weeks, new Date(2026, 9, 1))).toBe("2026-W40")
    })

    it("starts a monthly plan in the current month, or its first period when today is outside it", () => {
      const months: TimePeriod[] = [
        { year: 2026, month: 9, period_key: "2026-09" },
        { year: 2026, month: 10, period_key: "2026-10" },
      ]
      expect(default_start_period_key(months, new Date(2026, 9, 1))).toBe("2026-10")
      expect(default_start_period_key(months, new Date(2028, 0, 1))).toBe("2026-09")
      expect(default_start_period_key([], new Date(2028, 0, 1))).toBe("")
    })
  })

  describe("periods_from", () => {
    const weeks: TimePeriod[] = ["2027-W05", "2027-W06", "2027-W07"].map((period_key: string) => ({
      year: 2027,
      month: 2,
      period_key,
      grain: "week",
    }))

    it("offers only the start period and later as an end", () => {
      expect(periods_from(weeks, "2027-W06").map((p: TimePeriod) => p.period_key)).toEqual(["2027-W06", "2027-W07"])
    })

    it("offers every period until a start is picked", () => {
      expect(periods_from(weeks, "")).toHaveLength(3)
    })
  })
}
